import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Star, Disc3, Music, MessageSquare, Heart, Loader2, Lock, MapPin, Headphones, Users, Instagram, Youtube } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";

function BadgePill({ label, icon: Icon, color }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${color}`}>
      <Icon size={11} /> {label}
    </span>
  );
}

const SOCIAL_ICON_MAP = {
  instagram: Instagram,
  tiktok: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.77 0 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.3a6.34 6.34 0 0 0 10.09 5.12 6.34 6.34 0 0 0 2.24-4.78V8.69a8.17 8.17 0 0 0 4.78 1.52V6.78a4.85 4.85 0 0 1-1.01-.09z"/>
    </svg>
  ),
  youtube: Youtube,
  soundcloud: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M1.175 12.225c-.042 0-.08.03-.083.07l-.445 4.154.445 4.029c.003.04.04.07.083.07.04 0 .077-.03.08-.07l.503-4.029-.503-4.155c-.003-.04-.04-.07-.08-.07zm1.558-.57c-.05 0-.094.038-.098.088l-.39 4.636.39 3.89c.004.05.048.088.098.088.05 0 .094-.038.097-.088l.443-3.89-.443-4.636c-.003-.05-.047-.088-.097-.088zm1.588-.257c-.058 0-.107.047-.11.107l-.337 4.893.337 3.764c.003.06.052.107.11.107.06 0 .108-.047.11-.107l.384-3.764-.384-4.893c-.002-.06-.05-.107-.11-.107zm1.55-.165c-.067 0-.123.055-.126.123l-.283 5.058.283 3.648c.003.068.06.123.126.123.067 0 .123-.055.125-.123l.322-3.648-.322-5.058c-.002-.068-.058-.123-.125-.123zm1.538-.053c-.075 0-.138.062-.14.138l-.23 5.11.23 3.546c.002.076.066.138.14.138.075 0 .138-.062.14-.138l.261-3.546-.261-5.11c-.002-.076-.065-.138-.14-.138zm4.553-2.05c-.09-.024-.183-.036-.276-.036-1.123 0-2.037.912-2.037 2.037 0 .12.01.238.03.354L9.8 16.87l.103 3.42c.002.1.084.18.185.18.1 0 .182-.08.184-.18l.117-3.42-.117-8.98c-.002-.1-.084-.18-.185-.18zm1.53-.7c-.1 0-.182.08-.183.18l-.1 9.68.1 3.41c.001.1.083.18.183.18.1 0 .182-.08.183-.18l.113-3.41-.113-9.68c-.001-.1-.083-.18-.183-.18zm1.53-.36c-.11 0-.2.09-.2.2l-.086 10.04.086 3.39c0 .11.09.2.2.2s.2-.09.2-.2l.1-3.39-.1-10.04c0-.11-.09-.2-.2-.2zm2.07-.45c-.123 0-.224.1-.225.224l-.07 10.49.07 3.36c.001.123.102.224.225.224.123 0 .224-.1.225-.224l.08-3.36-.08-10.49c-.001-.123-.102-.224-.225-.224zm4.12 3.62c-.303 0-.593.06-.856.167-.177-1.986-1.843-3.532-3.87-3.532-.492 0-.96.095-1.39.267l-.003 14.67c.001.117.096.21.213.21h5.906c1.38 0 2.5-1.12 2.5-2.5v-6.78c0-1.294-1.05-2.502-2.5-2.502z"/>
    </svg>
  ),
};

export default function UserPublicProfilePage() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notPublic, setNotPublic] = useState(false);

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    api.get(`/community/profile/${encodeURIComponent(username)}`)
      .then(res => { setProfile(res.data); setLoading(false); })
      .catch(err => {
        setLoading(false);
        if (err.response?.status === 404) setNotFound(true);
        else setNotPublic(true);
      });
  }, [username]);

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("de", { day: "2-digit", month: "short", year: "numeric" });
  };

  const avatarUrl = profile?.avatar_url && !profile.avatar_url.includes("gravatar.com") ? profile.avatar_url : null;
  const initials = (profile?.username || "?").slice(0, 2).toUpperCase();

  return (
    <>
      <Helmet>
        <title>{profile?.username ? `${profile.username} – Community | HUWND` : `${username} | HUWND`}</title>
        <meta name="description" content={profile?.bio || `${username} ist Mitglied der HUWND Hands-Up Community.`} />
        <link rel="canonical" href={`https://handsupwillneverdie.de/user/${username}`} />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:title" content={profile?.username ? `${profile.username} – Community | HUWND` : `${username} | HUWND`} />
        <meta property="og:url" content={`https://handsupwillneverdie.de/user/${username}`} />
      </Helmet>
      <div className="min-h-screen bg-[#080808] text-white">
        <Header />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 sm:pb-20">
          {loading && (
            <div className="flex justify-center py-24">
              <Loader2 size={32} className="animate-spin text-zinc-600" />
            </div>
          )}

          {!loading && notPublic && (
            <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
              {/* Blurred background shapes */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-8 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-zinc-700/30 blur-3xl" />
                <div className="absolute top-24 left-1/2 -translate-x-1/2 w-80 h-4 rounded-full bg-zinc-600/10 blur-xl" />
                {/* Fake blurred profile skeleton */}
                <div className="absolute top-0 left-0 right-0 flex flex-col sm:flex-row items-start gap-8 pb-12 blur-sm opacity-20 select-none pointer-events-none">
                  <div className="w-36 h-36 rounded-2xl bg-zinc-700 flex-shrink-0" />
                  <div className="flex-1 space-y-3 pt-4">
                    <div className="h-8 w-48 bg-zinc-700 rounded-xl" />
                    <div className="h-4 w-32 bg-zinc-800 rounded-lg" />
                    <div className="h-3 w-64 bg-zinc-800 rounded-lg mt-6" />
                    <div className="h-3 w-48 bg-zinc-800 rounded-lg" />
                  </div>
                </div>
              </div>

              {/* Lock card */}
              <div className="relative z-10 flex flex-col items-center text-center px-6 py-10 rounded-3xl max-w-sm w-full"
                style={{
                  background: "rgba(12,12,12,0.85)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 24px 64px rgba(0,0,0,0.7), 0 1px 0 rgba(255,255,255,0.04) inset",
                  backdropFilter: "blur(24px)",
                }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <Lock size={22} className="text-zinc-400" />
                </div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">@{username}</p>
                <h2 className="text-xl font-black text-white mb-2">Profil nicht öffentlich</h2>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  Dieses Mitglied hat sein Profil nicht für die Öffentlichkeit freigegeben.
                </p>
                <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-sm text-zinc-400 hover:text-white transition-colors">
                  ← Zurück zur Startseite
                </Link>
              </div>
            </div>
          )}

          {!loading && notFound && (
            <div className="text-center py-24">
              <Lock size={40} className="text-zinc-700 mx-auto mb-4" />
              <h1 className="text-2xl font-black text-white mb-2">Profil nicht gefunden</h1>
              <p className="text-zinc-500 text-sm">Kein Benutzer mit dem Namen «{username}» gefunden.</p>
              <Link to="/" className="inline-block mt-6 text-rose-400 hover:text-rose-300 text-sm transition-colors">← Zurück zur Startseite</Link>
            </div>
          )}

          {!loading && profile && (
            <div className="space-y-12">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start gap-8 pb-12">
                <div className="relative flex-shrink-0">
                  <div className={`w-36 h-36 sm:w-44 sm:h-44 rounded-2xl overflow-hidden shadow-2xl ring-4 ${profile.is_artist ? "ring-blue-500/40" : profile.is_label_manager ? "ring-emerald-500/30" : profile.is_supporter ? "ring-rose-500/40" : "ring-zinc-800/60"}`}>
                    {avatarUrl
                      ? <img src={avatarUrl} alt={profile.username} className="w-full h-full object-cover" />
                      : (
                        <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                          <span className="text-5xl font-black text-zinc-500">{initials}</span>
                        </div>
                      )
                    }
                  </div>
                  {profile.is_artist && (
                    <span className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-blue-600 rounded-full border-2 border-zinc-900 flex items-center justify-center shadow-[0_0_10px_rgba(59,130,246,0.4)]">
                      <Disc3 size={13} className="text-white" />
                    </span>
                  )}
                  {profile.is_label_manager && !profile.is_artist && (
                    <span className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full border-2 border-zinc-900 flex items-center justify-center"
                      style={{ background: "#10b981", boxShadow: "0 0 10px rgba(16,185,129,0.4)" }}>
                      <Disc3 size={13} className="text-white" />
                    </span>
                  )}
                  {profile.is_supporter && (
                    <span className={`absolute -bottom-1.5 ${(profile.is_artist || profile.is_label_manager) ? '-left-1.5' : '-right-1.5'} w-7 h-7 bg-rose-600 rounded-full border-2 border-zinc-900 flex items-center justify-center shadow-[0_0_10px_rgba(225,29,72,0.4)]`}>
                      <Star size={11} className="text-white fill-white" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {profile.role === "developer" && (
                      <span className="text-xs font-black tracking-widest uppercase text-orange-400">Admin</span>
                    )}
                    {profile.role === "developer" && (
                      <span className="text-xs font-black tracking-widest uppercase text-emerald-400">Gründer</span>
                    )}
                    {profile.role === "admin" && (
                      <span className="text-xs font-black tracking-widest uppercase text-amber-400">Team</span>
                    )}
                    {profile.is_artist && (
                      <BadgePill label="Artist" icon={Disc3} color="bg-blue-500/15 text-blue-400 border border-blue-500/20" />
                    )}
                    {profile.is_label_manager && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full"
                        style={{ background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.25)" }}>
                        <Disc3 size={10} /> Label Manager
                      </span>
                    )}
                    {profile.is_supporter && (
                      <BadgePill label="Supporter" icon={Star} color="bg-rose-500/15 text-rose-400 border border-rose-500/20" />
                    )}
                    {profile.member_since && (
                      <span className="text-xs text-zinc-600">Mitglied seit {formatDate(profile.member_since)}</span>
                    )}
                  </div>

                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-heading leading-none mb-4 tracking-tight">
                    {profile.username}
                  </h1>

                  {/* Meta-Infos */}
                  <div className="flex flex-wrap items-center gap-5 text-sm mb-2">
                    {profile.location && (
                      <span className="flex items-center gap-1.5 text-zinc-400">
                        <MapPin size={13} className="text-zinc-600" /> {profile.location}
                      </span>
                    )}
                    {profile.genre && (
                      <span className="flex items-center gap-1.5 text-zinc-400">
                        <Headphones size={13} className="text-zinc-600" /> {profile.genre}
                      </span>
                    )}
                  </div>

                  {/* Social Links */}
                  {profile.social_links && Object.keys(profile.social_links).some(k => profile.social_links[k]) && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {Object.entries(profile.social_links).filter(([, v]) => v).map(([platform, url]) => {
                        const Icon = SOCIAL_ICON_MAP[platform];
                        return (
                          <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                            className="text-zinc-500 hover:text-zinc-300 transition-colors capitalize text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.06]">
                            {Icon && <Icon size={12} />} {platform}
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {/* Artist-Profile-Links: Haupt + weitere */}
                  {(() => {
                    const allIds = profile.artist_ids?.length ? profile.artist_ids : (profile.artist_id ? [profile.artist_id] : []);
                    if (!allIds.length) return null;
                    return (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {allIds.map((aid, idx) => {
                          const info = profile.artist_slugs?.[aid];
                          const name = info?.name || (idx === 0 ? profile.artist_name : null);
                          const slug = info?.slug || (idx === 0 ? profile.artist_slug : null);
                          if (!slug) return null;
                          return (
                            <Link key={aid} to={`/artist/${slug}`}
                              className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors px-3 py-1.5 rounded-xl ${
                                idx === 0
                                  ? "bg-blue-500/10 text-blue-400 hover:text-blue-300 hover:bg-blue-500/15"
                                  : "bg-white/[0.04] text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.07]"
                              }`}
                            >
                              <Music size={13} />
                              {name ? `${name}` : "Artist-Profil"} →
                              {idx === 0 && allIds.length > 1 && <span className="text-[10px] text-blue-600 font-bold uppercase ml-0.5">Haupt</span>}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Label-Profil-Link */}
                  {profile.is_label_manager && profile.label_name && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Link
                        to={profile.label_slug ? `/label/${profile.label_slug}` : "/label-studio"}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors px-3 py-1.5 rounded-xl"
                        style={{ background: "rgba(16,185,129,0.08)", color: "#10b981" }}
                      >
                        <Disc3 size={13} />
                        Label Manager von {profile.label_name} →
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Bio */}
              {profile.bio && (
                <div className="max-w-3xl">
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-4">Über {profile.username}</h2>
                  <p className="text-zinc-400 text-sm sm:text-base leading-[1.9] whitespace-pre-line">{profile.bio}</p>
                </div>
              )}

              {/* Lieblingskünstler */}
              {profile.fav_artists_text && (
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-4 flex items-center gap-1.5">
                    <Heart size={11} /> Lieblingskünstler
                  </h2>
                  <p className="text-zinc-400 text-sm sm:text-base">{profile.fav_artists_text}</p>
                </div>
              )}

              {/* Followed Artists */}
              {profile.followed_artists?.length > 0 && (
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-4 flex items-center gap-1.5">
                    <Users size={11} /> Gefolgte Artists ({profile.followed_artists.length})
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.followed_artists.map(artist => (
                      <Link key={artist.id} to={`/artist/${artist.slug}`}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-white/5 hover:border-white/15 hover:bg-zinc-800 transition-all text-sm text-zinc-300">
                        {artist.bild_url && (
                          <img src={artist.bild_url} alt={artist.name} className="w-5 h-5 rounded-full object-cover" />
                        )}
                        {artist.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Kommentare mit Release-Link */}
              {profile.comments?.length > 0 && (
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-4 flex items-center gap-1.5">
                    <MessageSquare size={11} /> Kommentare ({profile.comments.length})
                  </h2>
                  <div className="space-y-2">
                    {profile.comments.map((c, i) => (
                      <div key={i} className="bg-zinc-900/60 border border-white/5 rounded-xl px-4 py-3">
                        {c.release_slug && (
                          <Link
                            to={`/release/${c.release_slug}`}
                            className="text-[11px] text-zinc-600 hover:text-rose-400 transition-colors mb-1.5 flex items-center gap-1"
                          >
                            <Music size={9} />
                            {c.release_interpret ? `${c.release_interpret} – ` : ""}{c.release_titel || "Release ansehen"} →
                          </Link>
                        )}
                        <p className="text-zinc-300 text-sm leading-relaxed">{c.text}</p>
                        <p className="text-zinc-700 text-xs mt-1.5">{formatDate(c.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!profile.bio && !profile.fav_artists_text && profile.comments?.length === 0 && profile.followed_artists?.length === 0 && (
                <div className="text-center py-10 text-zinc-600 text-sm">
                  Noch keine öffentlichen Aktivitäten.
                </div>
              )}
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}
